import {
  IErrorApiResponse,
  IResource,
  ISuccessApiResponse,
  ResourceCreateFunction,
  ResourceType,
} from '@wyz/types';
import { notify } from '@wyz/ui';
import { BlobHelpers, UseMutationResult, useMutation } from '@wyz/utils';
import * as React from 'react';
import {
  useApi,
  useApplicationContext,
  useConfig,
  useI18n,
} from '~/common/context';
import { CommonErrorCodesEnumType, TranslationKeys } from '~/common/types';
import { useCommonErrorTranslation } from '~/common/hooks';

type Handlers = {
  uploadOne: (file: File, resourceType: ResourceType) => Promise<IResource>;
  uploadMany: ResourceCreateFunction;
  getDownloadablePath: (key: string) => void;
};

type ErrorResponse = IErrorApiResponse<unknown, keyof TranslationKeys>;

type UploadInput = {
  files: File[];
  resourceType: ResourceType;
};

export const useManageTenantResources = (): {
  handlers: Handlers;
  mutation: UseMutationResult<
    ISuccessApiResponse<Array<IResource>>,
    ErrorResponse,
    UploadInput,
    unknown
  >;
} => {
  const { t } = useI18n();
  const { resolveErrorKey } = useCommonErrorTranslation();
  const { callCoreApi } = useApi();
  const config = useConfig();
  const { auth } = useApplicationContext();

  const getDownloadablePath = React.useCallback(
    (id: string): void => {
      BlobHelpers.download({
        url: `${config.apiUrl}/tenants/api/wtb/tenants/${config.tenantCode}/resources/${id}`,
        token: auth.user?.access_token as string,
      }).catch(() => {});
    },
    [auth.user?.access_token, config.apiUrl, config.tenantCode],
  );

  const mutation = useMutation<
    ISuccessApiResponse<Array<IResource>>,
    ErrorResponse,
    UploadInput
  >({
    mutationFn: (data) => {
      const formData = new FormData();
      data.files.forEach((file, index) => {
        formData.append(`files${index}`, file, file.name);
      });
      return callCoreApi(
        `/tenants/api/wtb/tenants/${config.tenantCode}/resources?type=${data.resourceType}`,
        {
          body: formData,
          method: 'POST',
          asJson: false,
        },
      );
    },
    retry: false,
  });

  const run: ResourceCreateFunction = React.useCallback(
    async (data: File[], resourceType: ResourceType) => {
      try {
        const response = await mutation.mutateAsync({
          files: data,
          resourceType,
        });
        return response.data;
      } catch (error) {
        const axiosError = error as ErrorResponse;
        const errorCode = axiosError.errors
          .at(0)
          // @ts-expect-error - no shape defined for errors
          ?.errors.at(0) as CommonErrorCodesEnumType;
        notify.error(t(resolveErrorKey(errorCode)), {
          type: 'error',
          id: 'toast-error-tenant-resource-upload',
        });
        throw error;
      }
    },
    [mutation, t, resolveErrorKey],
  );

  const uploadOne: Handlers['uploadOne'] = React.useCallback(
    async (file, resourceType) => {
      const [resource] = await run([file], resourceType);
      if (!resource) {
        throw new Error('Resource not found');
      }
      return resource;
    },
    [run],
  );
  const uploadMany: Handlers['uploadMany'] = React.useCallback(
    async (files, resourceType) => {
      return files.length > 0 ? await run(files, resourceType) : [];
    },
    [run],
  );

  const handlers: Handlers = React.useMemo(
    () => ({
      uploadOne,
      uploadMany,
      getDownloadablePath,
    }),
    [getDownloadablePath, uploadMany, uploadOne],
  );
  return {
    handlers,
    mutation,
  };
};
